import type {
  AccountBalance,
  AccountBalancesResponse,
  ApiAccountBalance,
  ArbitrageOpportunity,
  Order,
  StrategyStatus,
  CandleData,
  TechnicalIndicators,
  SupportResistance,
  Trade,
  DepthAnalysis,
  DepthHistoryPoint,
  KillSwitchProcessInfo,
  KillSwitchProcesses,
  KillSwitchState,
  KillSwitchStatusResponse,
  DexPricesResponse,
  DexPricesSummary,
  DexPriceEntry,
  ServiceActivityState,
  ServiceRuntimeState,
  SpreadMonitorResponse,
  SystemOverviewResponse,
} from '../types/trade';

const API_BASE = '/api';

async function fetchJson<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`);
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

async function postJson<T>(endpoint: string, data: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseNullableNumber(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function parseNumber(value: unknown, field: string): number {
  if (typeof value !== 'number') {
    throw new Error(`Invalid numeric field for ${field}`);
  }
  return value;
}

function parseStringMap(value: unknown): Record<string, string> {
  if (!isRecord(value)) {
    throw new Error('Invalid string map payload');
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => {
      if (typeof entryValue !== 'string') {
        throw new Error(`Invalid string-map entry for ${key}`);
      }
      return [key, entryValue];
    })
  );
}

function parseOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function isRuntimeServiceState(value: unknown): value is ServiceRuntimeState {
  return value === 'active' || value === 'inactive' || value === 'gated';
}

function isActivityServiceState(value: unknown): value is ServiceActivityState {
  return value === 'active' || value === 'inactive';
}

function parseRuntimeServiceMap(value: unknown): Record<string, ServiceRuntimeState> {
  if (!isRecord(value)) {
    throw new Error('Invalid runtime services payload');
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => {
      if (!isRuntimeServiceState(entryValue)) {
        throw new Error(`Invalid runtime service state for ${key}`);
      }
      return [key, entryValue];
    })
  );
}

function parseActivityServiceMap(value: unknown): Record<string, ServiceActivityState> {
  if (!isRecord(value)) {
    throw new Error('Invalid activity services payload');
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => {
      if (!isActivityServiceState(entryValue)) {
        throw new Error(`Invalid activity service state for ${key}`);
      }
      return [key, entryValue];
    })
  );
}

function deriveActivityServices(
  value: Record<string, ServiceRuntimeState>
): Record<string, ServiceActivityState> {
  return Object.fromEntries(
    Object.entries(value).map(([key, state]) => [key, state === 'active' ? 'active' : 'inactive'])
  );
}

function parseKillSwitchState(value: unknown): KillSwitchState {
  if (typeof value === 'string') {
    return { status: value, timestamp: null };
  }

  if (!isRecord(value) || typeof value.status !== 'string') {
    throw new Error('Invalid kill-switch state payload');
  }

  return {
    status: value.status,
    timestamp: typeof value.timestamp === 'string' ? value.timestamp : null,
    reason: typeof value.reason === 'string' ? value.reason : undefined,
  };
}

function parseKillSwitchProcessInfo(value: unknown): KillSwitchProcessInfo {
  if (!isRecord(value)) {
    throw new Error('Invalid kill-switch process payload');
  }

  return {
    running: Boolean(value.running),
    pid: typeof value.pid === 'number' ? value.pid : null,
    description: typeof value.description === 'string' ? value.description : 'Unknown process',
  };
}

function parseKillSwitchProcessGroup(value: unknown): Record<string, KillSwitchProcessInfo> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([name, processInfo]) => [name, parseKillSwitchProcessInfo(processInfo)])
  );
}

function parseKillSwitchProcesses(value: unknown): KillSwitchProcesses {
  if (!isRecord(value)) {
    return { critical: {}, monitoring: {} };
  }

  return {
    critical: parseKillSwitchProcessGroup(value.critical),
    monitoring: parseKillSwitchProcessGroup(value.monitoring),
    timestamp: typeof value.timestamp === 'string' ? value.timestamp : undefined,
  };
}

function parseKillSwitchStatusResponse(value: unknown): KillSwitchStatusResponse {
  if (!isRecord(value) || typeof value.status !== 'string' || typeof value.timestamp !== 'string') {
    throw new Error('Invalid kill-switch status response');
  }

  return {
    status: value.status,
    kill_state: parseKillSwitchState(value.kill_state),
    processes: parseKillSwitchProcesses(value.processes),
    timestamp: value.timestamp,
    warning: typeof value.warning === 'string' ? value.warning : undefined,
  };
}

export function parseSpreadMonitorResponse(value: unknown): SpreadMonitorResponse {
  if (!isRecord(value) || typeof value.status !== 'string' || typeof value.symbol !== 'string') {
    throw new Error('Invalid spread-monitor response');
  }

  const warnings = Array.isArray(value.warnings)
    ? value.warnings.filter((warning): warning is string => typeof warning === 'string')
    : [];

  return {
    status: value.status,
    symbol: value.symbol,
    spread_percentage: parseNullableNumber(value.spread_percentage),
    bid: parseNullableNumber(value.bid),
    ask: parseNullableNumber(value.ask),
    depth_2pct: parseNullableNumber(value.depth_2pct),
    depth_proprietary: parseNullableNumber(value.depth_proprietary),
    depth_external: parseNullableNumber(value.depth_external),
    depth_proprietary_pct: parseNullableNumber(value.depth_proprietary_pct),
    depth_external_pct: parseNullableNumber(value.depth_external_pct),
    compliant: value.compliant === true,
    warnings,
  };
}

function parseDexPriceEntry(value: unknown): DexPriceEntry {
  if (!isRecord(value) || typeof value.timestamp !== 'string') {
    throw new Error('Invalid dex-price entry');
  }

  return {
    price: parseNumber(value.price, 'price'),
    timestamp: value.timestamp,
    age_seconds: parseNumber(value.age_seconds, 'age_seconds'),
    fresh: value.fresh === true,
  };
}

function parseDexPriceMap(value: unknown): Record<string, Record<string, DexPriceEntry>> {
  if (!isRecord(value)) {
    throw new Error('Invalid dex-prices payload');
  }

  return Object.fromEntries(
    Object.entries(value).map(([chain, dexEntries]) => {
      if (!isRecord(dexEntries)) {
        throw new Error(`Invalid dex entries for ${chain}`);
      }

      return [
        chain,
        Object.fromEntries(
          Object.entries(dexEntries).map(([dex, entry]) => [dex, parseDexPriceEntry(entry)])
        ),
      ];
    })
  );
}

function parseDexPricesSummary(value: unknown): DexPricesSummary {
  if (!isRecord(value)) {
    return {};
  }

  return {
    average: parseOptionalNumber(value.average),
    min: parseOptionalNumber(value.min),
    max: parseOptionalNumber(value.max),
    count: parseOptionalNumber(value.count),
  };
}

function parseArbitrageOpportunity(value: unknown): ArbitrageOpportunity {
  if (
    !isRecord(value) ||
    typeof value.type !== 'string' ||
    typeof value.chain !== 'string' ||
    typeof value.dex !== 'string'
  ) {
    throw new Error('Invalid arbitrage opportunity');
  }

  return {
    type: value.type,
    chain: value.chain,
    dex: value.dex,
    profit_pct: parseNumber(value.profit_pct, 'profit_pct'),
    cex_price: parseNumber(value.cex_price, 'cex_price'),
    dex_price: parseNumber(value.dex_price, 'dex_price'),
  };
}

export function parseDexPricesResponse(value: unknown): DexPricesResponse {
  if (!isRecord(value) || typeof value.status !== 'string' || typeof value.timestamp !== 'string') {
    throw new Error('Invalid dex-prices response');
  }

  return {
    status: value.status,
    timestamp: value.timestamp,
    dex_prices: parseDexPriceMap(value.dex_prices ?? {}),
    cex_price: parseNullableNumber(value.cex_price),
    summary: parseDexPricesSummary(value.summary),
    arbitrage: Array.isArray(value.arbitrage)
      ? value.arbitrage.map((entry) => parseArbitrageOpportunity(entry))
      : [],
  };
}

function parseApiAccountBalance(value: unknown): ApiAccountBalance {
  if (!isRecord(value) || typeof value.account !== 'string') {
    throw new Error('Invalid account-balance payload');
  }

  return {
    account: value.account,
    fco: parseNumber(value.fco, 'fco'),
    fco_total: parseNumber(value.fco_total, 'fco_total'),
    fco_frozen: parseNumber(value.fco_frozen, 'fco_frozen'),
    usdt: parseNumber(value.usdt, 'usdt'),
    usdt_total: parseNumber(value.usdt_total, 'usdt_total'),
    usdt_frozen: parseNumber(value.usdt_frozen, 'usdt_frozen'),
    total_usd: parseNumber(value.total_usd, 'total_usd'),
    lastUpdated: typeof value.lastUpdated === 'string' ? value.lastUpdated : null,
    ageSeconds: typeof value.ageSeconds === 'number' ? value.ageSeconds : null,
  };
}

export function parseAccountBalancesResponse(value: unknown): AccountBalancesResponse {
  if (
    !isRecord(value) ||
    !Array.isArray(value.accounts) ||
    typeof value.collection_interval_seconds !== 'number'
  ) {
    throw new Error('Invalid account-balances response');
  }

  return {
    accounts: value.accounts.map((entry) => parseApiAccountBalance(entry)),
    last_updated: typeof value.last_updated === 'string' ? value.last_updated : null,
    collection_interval_seconds: value.collection_interval_seconds,
  };
}

export function parseSystemOverviewResponse(value: unknown): SystemOverviewResponse {
  if (!isRecord(value) || typeof value.kill_switch !== 'string') {
    throw new Error('Invalid system-overview response');
  }

  const servicesSource = value.runtime_services ?? value.services;
  const runtimeServices = parseRuntimeServiceMap(servicesSource);
  const services = value.services ? parseRuntimeServiceMap(value.services) : runtimeServices;
  const activityServices = value.activity_services
    ? parseActivityServiceMap(value.activity_services)
    : deriveActivityServices(runtimeServices);

  return {
    kill_switch: value.kill_switch,
    spread_compliant: typeof value.spread_compliant === 'boolean' ? value.spread_compliant : null,
    spread_pct: parseNullableNumber(value.spread_pct),
    peg_compliance_pct: parseNullableNumber(value.peg_compliance_pct),
    fco_price: parseNullableNumber(value.fco_price),
    containers: parseStringMap(value.containers ?? {}),
    services,
    runtime_services: runtimeServices,
    activity_services: activityServices,
  };
}

export const api = {
  // Account & Balances
  async getBalances(): Promise<AccountBalancesResponse> {
    const payload = await fetchJson<unknown>('/balances');
    return parseAccountBalancesResponse(payload);
  },

  async getBalance(account: string): Promise<AccountBalance> {
    return fetchJson(`/balances/${account}`);
  },

  // Market Data
  async getMarketData(symbol: string = 'fco_usdt'): Promise<any> {
    return fetchJson(`/market/${symbol}`);
  },

  async getCandles(
    symbol: string = 'fco_usdt',
    interval: string = '1h',
    limit: number = 500
  ): Promise<CandleData[]> {
    return fetchJson(`/candles/${symbol}?interval=${interval}&limit=${limit}`);
  },

  // Order Book
  async getOrderBook(symbol: string = 'fco_usdt'): Promise<any> {
    return fetchJson(`/orderbook/${symbol}`);
  },

  // Spread
  async getSpreadStatus(symbol: string = 'fco_usdt'): Promise<SpreadMonitorResponse> {
    const payload = await fetchJson<unknown>(`/spread-monitor/${symbol}`);
    return parseSpreadMonitorResponse(payload);
  },

  // Orders
  async getOpenOrders(account?: string): Promise<Order[]> {
    const endpoint = account ? `/orders/open/live?account=${account}` : '/orders/open/live';
    return fetchJson(endpoint);
  },

  async cancelOrder(orderId: string, account: string, symbol: string = 'fco_usdt'): Promise<{ success: boolean }> {
    return postJson('/orders/cancel', { orderId, account, symbol });
  },

  async placeOrder(order: {
    account: string;
    symbol: string;
    side: 'buy' | 'sell';
    type: 'limit' | 'market';
    price?: number;
    quantity: number;
  }): Promise<{ orderId: string; success: boolean }> {
    return postJson('/orders/place', order);
  },

  // Trades
  async getRecentTrades(
    symbol: string = 'fco_usdt',
    limit: number = 50
  ): Promise<Trade[]> {
    return fetchJson(`/trades/${symbol}?limit=${limit}`);
  },

  // Strategies
  async getStrategies(): Promise<StrategyStatus[]> {
    return fetchJson('/strategies');
  },

  async toggleStrategy(
    strategyId: string,
    enabled: boolean
  ): Promise<{ success: boolean }> {
    return postJson(`/strategies/${strategyId}/toggle`, { enabled });
  },

  async updateStrategyParams(
    strategyId: string,
    params: Record<string, unknown>
  ): Promise<{ success: boolean }> {
    return postJson(`/strategies/${strategyId}/params`, params);
  },

  // Technical Analysis
  async getIndicators(symbol: string = 'fco_usdt'): Promise<TechnicalIndicators> {
    return fetchJson(`/analysis/indicators/${symbol}`);
  },

  async getSupportResistance(symbol: string = 'fco_usdt'): Promise<SupportResistance> {
    return fetchJson(`/analysis/levels/${symbol}`);
  },

  // Positions
  async getPositions(symbol: string = 'fco_usdt'): Promise<{
    positions: Array<{
      account: string;
      symbol: string;
      baseAsset: string;
      quantity: number;
      costBasis: number;
      currentPrice: number;
      positionValue: number;
      costValue: number;
      unrealizedPnl: number;
      unrealizedPnlPct: number;
      usdtBalance: number;
      totalValue: number;
      historicalBuyVolume: number;
      historicalSellVolume: number;
      netTradingPnl: number;
      error?: string;
    }>;
    currentPrice: number;
    timestamp: string;
  }> {
    return fetchJson(`/positions?symbol=${symbol}`);
  },

  // Kill Switch
  async getKillSwitchStatus(): Promise<KillSwitchStatusResponse> {
    const payload = await fetchJson<unknown>('/kill-switch/status');
    return parseKillSwitchStatusResponse(payload);
  },

  async activateKillSwitch(reason: string): Promise<{ success: boolean }> {
    return postJson('/kill-switch/activate', { reason });
  },

  async deactivateKillSwitch(): Promise<{ success: boolean }> {
    return postJson('/kill-switch/deactivate', {});
  },

  // Conditional Orders (Stop-Loss, Take-Profit, OCO)
  async getConditionalOrders(params?: {
    symbol?: string;
    account?: string;
    status?: string;
    limit?: number;
  }): Promise<{ orders: Array<{
    id: string;
    account: string;
    symbol: string;
    order_type: string;
    side: string;
    trigger_condition: string;
    trigger_price: number;
    limit_price: number | null;
    quantity: number;
    status: string;
    oco_pair_id: string | null;
    created_at: string;
    triggered_at: string | null;
    executed_at: string | null;
    executed_order_id: string | null;
    executed_price: number | null;
    executed_quantity: number | null;
    error_message: string | null;
    expires_at: string | null;
    notes: string | null;
  }>; count: number }> {
    const queryParams = new URLSearchParams();
    if (params?.symbol) queryParams.append('symbol', params.symbol);
    if (params?.account) queryParams.append('account', params.account);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return fetchJson(`/orders/conditional${query ? '?' + query : ''}`);
  },

  async createStopLoss(order: {
    account: string;
    side: 'buy' | 'sell';
    trigger_price: number;
    quantity: number;
    symbol?: string;
    limit_price?: number;
    expires_in_hours?: number;
    notes?: string;
  }): Promise<{ success: boolean; order?: unknown; error?: string }> {
    return postJson('/orders/stop-loss', order);
  },

  async createTakeProfit(order: {
    account: string;
    side: 'buy' | 'sell';
    trigger_price: number;
    quantity: number;
    symbol?: string;
    limit_price?: number;
    expires_in_hours?: number;
    notes?: string;
  }): Promise<{ success: boolean; order?: unknown; error?: string }> {
    return postJson('/orders/take-profit', order);
  },

  async createOCOOrder(order: {
    account: string;
    side: 'buy' | 'sell';
    stop_trigger_price: number;
    take_trigger_price: number;
    quantity: number;
    symbol?: string;
    stop_limit_price?: number;
    take_limit_price?: number;
    expires_in_hours?: number;
    notes?: string;
  }): Promise<{ success: boolean; oco_pair?: unknown; error?: string }> {
    return postJson('/orders/oco', order);
  },

  async cancelConditionalOrder(orderId: string): Promise<{ success: boolean; cancelled_ids?: string[]; error?: string }> {
    const response = await fetch(`${API_BASE}/orders/conditional/${orderId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },

  // Alert Management
  async getAlerts(params?: {
    severity?: string;
    acknowledged?: boolean;
    resolved?: boolean;
    hours?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ alerts: Array<{
    id: number;
    timestamp: string;
    severity: string;
    title: string;
    message: string;
    metric_name: string | null;
    metric_value: number | null;
    threshold_value: number | null;
    acknowledged: boolean;
    acknowledged_at: string | null;
    resolved: boolean;
    resolved_at: string | null;
  }>; count: number }> {
    const queryParams = new URLSearchParams();
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.acknowledged !== undefined) queryParams.append('acknowledged', String(params.acknowledged));
    if (params?.resolved !== undefined) queryParams.append('resolved', String(params.resolved));
    if (params?.hours) queryParams.append('hours', params.hours.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return fetchJson(`/alerts${query ? '?' + query : ''}`);
  },

  async getAlertStats(hours: number = 24): Promise<{
    period_hours: number;
    by_severity: { critical: number; warning: number; info: number };
    by_status: { total: number; unacknowledged: number; unresolved: number; in_progress: number };
    by_type: Array<{ title: string; count: number }>;
  }> {
    return fetchJson(`/alerts/stats?hours=${hours}`);
  },

  async acknowledgeAlert(alertId: number): Promise<{ success: boolean; alert_id?: number; error?: string }> {
    return postJson(`/alerts/${alertId}/acknowledge`, {});
  },

  async resolveAlert(alertId: number): Promise<{ success: boolean; alert_id?: number; error?: string }> {
    return postJson(`/alerts/${alertId}/resolve`, {});
  },

  async acknowledgeAlerts(alertIds: number[]): Promise<{ success: boolean; acknowledged_ids?: number[]; count?: number; error?: string }> {
    return postJson('/alerts/acknowledge', { alert_ids: alertIds });
  },

  async resolveAlerts(alertIds: number[]): Promise<{ success: boolean; resolved_ids?: number[]; count?: number; error?: string }> {
    return postJson('/alerts/resolve', { alert_ids: alertIds });
  },

  async createAlert(alert: {
    title: string;
    message: string;
    severity?: string;
    metric_name?: string;
    metric_value?: number;
    threshold_value?: number;
    send_notification?: boolean;
  }): Promise<{ success: boolean; alert?: unknown; error?: string }> {
    return postJson('/alerts', alert);
  },

  // Liquidity Depth
  async getDepthAnalysis(symbol: string = 'fco_usdt'): Promise<DepthAnalysis> {
    return fetchJson(`/depth/${symbol}`);
  },

  async getDepthHistory(symbol: string = 'fco_usdt', hours: number = 24): Promise<DepthHistoryPoint[]> {
    return fetchJson(`/depth/${symbol}/history?hours=${hours}`);
  },

  // Spread History (ROC-18)
  async getSpreadHistory(hours: number = 24): Promise<{
    data: Array<{ time: string; spread_pct: number | null; compliant: boolean; bid: number | null; ask: number | null; depth_2pct: number | null }>;
    stats: { count: number; min_spread: number | null; max_spread: number | null; avg_spread: number | null; compliance_pct: number; hours: number };
  }> {
    return fetchJson(`/spread-history?hours=${hours}`);
  },

  // Peg Summary (ROC-18)
  async getPegSummary(hours: number = 24): Promise<{
    pairs: Record<string, { total_checks: number; compliant_checks: number; failed_checks: number; compliance_pct: number; min_value: number; max_value: number; avg_pct: number; min_pct: number; max_pct: number; minimum: number }>;
    latest: Record<string, { current: number | null; percentage: number | null; compliant: boolean; time: string }>;
    overall_compliance_pct: number;
    total_checks: number;
    hours: number;
  }> {
    return fetchJson(`/peg-status/summary?hours=${hours}`);
  },

  // System Overview (ROC-18)
  async getSystemOverview(): Promise<SystemOverviewResponse> {
    const payload = await fetchJson<unknown>('/system-overview');
    return parseSystemOverviewResponse(payload);
  },

  async getWhaleAnalysis(): Promise<any> {
    return fetchJson("/whale-analysis");
  },

  async getWhaleHistory(hours: number = 168): Promise<any> {
    return fetchJson(`/whale-analysis/history?hours=${hours}`);
  },

  async getDexPrices(): Promise<any> {
    const payload = await fetchJson<unknown>("/dex-prices");
    return parseDexPricesResponse(payload);
  },

  // Developer Activity (ROC-22)
  async getDeveloperActivity(): Promise<any> {
    return fetchJson('/developer-activity');
  },
};
